import type { ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import { staticRelations } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import {
  createWorkerQueryExecutors,
  workerQueryCodecs,
} from "@-/dev.improve.haniverse.configs.worker/worker-query-executor";
import {
  createBatchedWorkerQuerySend,
  createSingleWorkerQuerySend,
} from "@-/dev.improve.haniverse.configs.worker/worker-query-batcher";
import { drizzle, type SqliteRemoteDatabase } from "@-/com.npmjs.drizzle-orm/sqlite-proxy";
import memoize, { memoizeClear } from "@-/com.npmjs.memoize";

type StaticRelations = typeof staticRelations;

/**
 * On web the static info data is NOT shipped to the browser. Instead of
 * importing the ~28MB `static.db` into OPFS, the web build queries it remotely
 * from the `haniverse-worker` Cloudflare Worker, which runs SQL against a D1 copy of
 * the same schema (see apps/haniverse-worker). The "connection" here is therefore
 * just the worker base URL — the actual transport is `fetch` inside
 * `getStaticDatabase`.
 *
 * This mirrors the native path's shape (`createStaticDatabaseConnection` →
 * `getStaticDatabase`) so `useGetStaticDatabase` is platform-agnostic.
 */
export interface StaticDatabaseConnection {
  workerUrl: string;
}

/** Resolved drizzle (remote sqlite-proxy) handle on the static connection. */
// rc.5: `SqliteRemoteDatabase<TRunResult, TRelations>` — the run result comes first.
export type StaticDatabaseHandle = SqliteRemoteDatabase<unknown, StaticRelations>;

/** Getter form of {@link StaticDatabaseHandle}; takes `import.meta.location` (or `null` to opt out of logging). */
export type StaticDatabase = (location: ImportMetaLocation) => StaticDatabaseHandle;

/**
 * `staticAbsolutePath` is the worker base URL on web — the shard importer
 * (`ensure-content-database-shards-from-assets.web.ts`) resolves
 * `EXPO_PUBLIC_HANIVERSE_WORKER_URL` and threads it through as the static database
 * "path". Keeping the value here lets `useGetStaticDatabase`'s single-flight cache
 * key on it just like the native filesystem path.
 */
export const createStaticDatabaseConnection = memoize(
  async (staticAbsolutePath: string): Promise<StaticDatabaseConnection> => ({
    workerUrl: staticAbsolutePath,
  }),
  { cacheKey: ([staticAbsolutePath]) => staticAbsolutePath },
);

/** Parity with the native variant; see its doc comment. No-op-ish on web. */
export function clearStaticDatabaseConnectionMemo(): void {
  memoizeClear(createStaticDatabaseConnection);
}

const TRAILING_SLASHES_RE = /\/+$/;

/**
 * On web every static-data mode is already served remotely from the worker, so
 * "online" mode is just the normal remote handle. Provided for parity with the
 * native variant (whose default handle is a local SQLite file) so the shared
 * `use-static-database` load path can call it on both platforms.
 */
export function getOnlineStaticDatabase(workerUrl: string): StaticDatabaseHandle {
  return getStaticDatabase({ workerUrl });
}

export function getStaticDatabase(connection: StaticDatabaseConnection): StaticDatabaseHandle {
  const baseUrl = connection.workerUrl.replace(TRAILING_SLASHES_RE, "");
  // Built ONCE per handle, not per query: the batcher's coalescing queue lives in this closure, so
  // a per-query instance would have nothing to batch with and silently degrade to one request each.
  const send = createBatchedWorkerQuerySend({
    batchUrl: `${baseUrl}/query/batch`,
    label: "static-db",
    sendSingle: createSingleWorkerQuerySend({
      label: "static-db",
      queryUrl: `${baseUrl}/query`,
    }),
  });
  // `createWorkerQueryExecutors` routes every statement through `executeWorkerQuery`, so a
  // `where col in (...)` over more than D1's 100-bound-parameter cap is split into several
  // under-cap statements and the row sets concatenated. Call sites can issue one natural
  // set-based read at any set size. The `send` above then coalesces those chunks — and every
  // other read in the same tick — into one POST.
  //
  // Which method a query uses is drizzle's choice: RQB reads (`findFirst`/`findMany`) go out as
  // `all` (any `LIMIT 1` lives in the SQL), which the worker always answers with an array; only
  // an explicit `db.get(...)` uses `get`, whose no-match `null` the executors turn back into
  // `undefined`. Full rationale + the regression test: `static/static-database.server.ts` and
  // `static/__tests__/static-database-server-transport.test.ts`.
  return drizzle<StaticRelations>(createWorkerQueryExecutors(send), {
    codecs: workerQueryCodecs,
    relations: staticRelations,
  });
}
